/**
 * Paying for Pro without connecting a wallet: a Solana Pay QR and the address for the exact
 * sum of USDC. The person pays from any wallet app; the page asks the server every few
 * seconds whether the transfer has landed, and says so when the account is credited.
 * Used by the payment dialog and the plan page (utils/payments.ts, utils/solanaPay.ts).
 */
import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, Copy, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { createPaymentIntent, checkPayment, paymentErrorText } from '../utils/payments';
import { CHECK_EVERY_MS, amountText, type PaymentRequest } from '../utils/solanaPay';
import { formatUsdc } from '../utils/pricing';

interface PayWithQrProps {
    /** Months of Pro being bought */
    credits: number;
    /** The account was credited: the transfer's signature and the credits now held */
    onPaid: (result: { signature: string; newCredits: number }) => void;
    /** A smaller layout, for the dialog */
    compact?: boolean;
}

type Stage =
    | { kind: 'asking' }
    | { kind: 'waiting'; request: PaymentRequest; checking: boolean }
    | { kind: 'expired'; request: PaymentRequest }
    | { kind: 'paid'; signature: string }
    | { kind: 'problem'; message: string };

/** A QR of the Solana Pay address, drawn on demand (the library loads with the first request) */
const useQr = (text: string | null): string | null => {
    const [dataUrl, setDataUrl] = useState<string | null>(null);
    useEffect(() => {
        let cancelled = false;
        setDataUrl(null);
        if (!text) return;
        import('qrcode')
            .then(qr => qr.toDataURL(text, { errorCorrectionLevel: 'M', margin: 1, width: 240, color: { dark: '#1A1917', light: '#FFFFFF' } }))
            .then(url => { if (!cancelled) setDataUrl(url); })
            .catch(() => { /* the address and the link remain */ });
        return () => { cancelled = true; };
    }, [text]);
    return dataUrl;
};

export const PayWithQr: React.FC<PayWithQrProps> = ({ credits, onPaid, compact }) => {
    const [stage, setStage] = useState<Stage>({ kind: 'asking' });
    const [copied, setCopied] = useState<'address' | 'amount' | null>(null);
    const [attempt, setAttempt] = useState(0);
    const request = stage.kind === 'waiting' || stage.kind === 'expired' ? stage.request : null;
    const qr = useQr(request?.url ?? null);
    const onPaidRef = useRef(onPaid);
    onPaidRef.current = onPaid;

    // A request for the chosen amount; a new amount or "Start again" asks for another
    useEffect(() => {
        let cancelled = false;
        setStage({ kind: 'asking' });
        createPaymentIntent(credits)
            .then(req => { if (!cancelled) setStage({ kind: 'waiting', request: req, checking: false }); })
            .catch(e => { if (!cancelled) setStage({ kind: 'problem', message: paymentErrorText(e, 'The payment request could not be made. Try again in a moment.') }); });
        return () => { cancelled = true; };
    }, [credits, attempt]);

    // Every few seconds, whether the transfer has landed
    const reference = request?.reference ?? null;
    useEffect(() => {
        if (!reference || stage.kind !== 'waiting') return;
        let cancelled = false;
        let busy = false;
        const look = async () => {
            if (busy || cancelled) return;
            busy = true;
            try {
                const state = await checkPayment(reference);
                if (cancelled) return;
                if (state.status === 'paid') {
                    setStage({ kind: 'paid', signature: state.signature });
                    onPaidRef.current({ signature: state.signature, newCredits: state.newCredits });
                } else if (state.status === 'expired') {
                    setStage(prev => (prev.kind === 'waiting' ? { kind: 'expired', request: prev.request } : prev));
                }
            } catch {
                // A check that fails is asked again on the next tick
            } finally {
                busy = false;
            }
        };
        const timer = window.setInterval(look, CHECK_EVERY_MS);
        return () => { cancelled = true; window.clearInterval(timer); };
    }, [reference, stage.kind]);

    const checkNow = async () => {
        if (stage.kind !== 'waiting' || stage.checking) return;
        setStage({ ...stage, checking: true });
        try {
            const state = await checkPayment(stage.request.reference);
            if (state.status === 'paid') {
                setStage({ kind: 'paid', signature: state.signature });
                onPaidRef.current({ signature: state.signature, newCredits: state.newCredits });
            } else if (state.status === 'expired') {
                setStage({ kind: 'expired', request: stage.request });
            } else {
                setStage({ kind: 'waiting', request: stage.request, checking: false });
            }
        } catch (e) {
            setStage({ kind: 'problem', message: paymentErrorText(e, 'The payment could not be checked. Try again in a moment.') });
        }
    };

    const copy = async (what: 'address' | 'amount', text: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(what);
            window.setTimeout(() => setCopied(null), 1500);
        } catch {
            // Nothing to say: the text is on the page to select
        }
    };

    if (stage.kind === 'asking') {
        return <p className="flex items-center justify-center gap-2 py-8 text-sm text-ink-soft" role="status"><Loader2 size={16} className="animate-spin" aria-hidden="true" />Preparing your payment request</p>;
    }

    if (stage.kind === 'problem') {
        return (
            <div className="ns-note block p-4" data-tone="danger" role="alert">
                <p className="flex items-center gap-2"><AlertCircle size={14} aria-hidden="true" />{stage.message}</p>
                <button type="button" className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat mt-3" onClick={() => setAttempt(a => a + 1)}>Try again</button>
            </div>
        );
    }

    if (stage.kind === 'paid') {
        return (
            <div className="ns-note p-4 items-center" data-tone="ok" role="status">
                <Check aria-hidden="true" />
                <div className="flex-1">
                    <p className="font-semibold">Payment received</p>
                    <p>{credits} month{credits === 1 ? '' : 's'} of Pro added to your account.</p>
                </div>
                <a className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat" href={`https://explorer.solana.com/tx/${stage.signature}`} target="_blank" rel="noopener noreferrer">
                    View transfer <ExternalLink size={14} aria-hidden="true" />
                </a>
            </div>
        );
    }

    const req = stage.request;
    const amount = amountText(req.amount);
    const expired = stage.kind === 'expired';
    const side = compact ? 168 : 200;

    return (
        <div className={`flex ${compact ? 'flex-col items-center' : 'flex-col sm:flex-row items-center sm:items-start'} gap-5`} data-pay-with-qr>
            {/* The QR: scanned by Phantom, Solflare, Backpack and the rest */}
            <div className="shrink-0 rounded-2xl bg-white p-3 shadow-hair border border-cream-300" aria-hidden={expired}>
                {qr ? (
                    <img src={qr} alt={`QR for a payment of ${amount} USDC`} width={side} height={side} className={`block rounded-lg ${expired ? 'opacity-30' : ''}`} />
                ) : (
                    <div className="grid place-items-center text-ink-faint" style={{ width: side, height: side }}><Loader2 size={20} className="animate-spin" aria-hidden="true" /></div>
                )}
            </div>

            <div className="min-w-0 flex-1 w-full">
                <p className="text-sm text-ink-soft">
                    Send exactly <strong className="font-semibold text-ink">{formatUsdc(req.amount)}</strong> on Solana to this address from any wallet. Scan the code, or copy the address and the amount.
                </p>

                <div className="ns-well mt-3 p-3">
                    <p className="ns-t-label">Address</p>
                    <div className="mt-1 flex items-center gap-2">
                        <code className="ns-t-mono min-w-0 flex-1 break-all text-xs text-ink">{req.recipient}</code>
                        <button type="button" className="ns-icon-btn ns-icon-btn-sm shrink-0" onClick={() => copy('address', req.recipient)} aria-label="Copy the address" title="Copy the address">
                            {copied === 'address' ? <Check /> : <Copy />}
                        </button>
                    </div>
                    <p className="ns-t-label mt-3">Amount</p>
                    <div className="mt-1 flex items-center gap-2">
                        <code className="ns-t-mono flex-1 text-sm text-ink">{amount} USDC</code>
                        <button type="button" className="ns-icon-btn ns-icon-btn-sm shrink-0" onClick={() => copy('amount', amount)} aria-label="Copy the amount" title="Copy the amount">
                            {copied === 'amount' ? <Check /> : <Copy />}
                        </button>
                    </div>
                </div>

                {expired ? (
                    <div className="ns-note mt-3 block p-3" data-tone="warn" role="status">
                        <p>This request has run out. If you already sent the payment, it is still yours: press Check again. Otherwise start a new request.</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                            <button type="button" className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat" onClick={() => setAttempt(a => a + 1)}>Start again</button>
                            <button type="button" className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat" onClick={() => setStage({ kind: 'waiting', request: req, checking: false })}>Check again</button>
                        </div>
                    </div>
                ) : (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        <a className="ns-btn ns-btn-sm ns-btn-primary ns-btn-flat" href={req.url}>Open in wallet</a>
                        <button type="button" className="ns-btn ns-btn-sm ns-btn-ghost ns-btn-flat" onClick={checkNow} disabled={stage.checking}>
                            {stage.checking ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={14} aria-hidden="true" />}
                            I have paid
                        </button>
                        <span className="ns-t-meta flex items-center gap-1.5" role="status"><Loader2 size={12} className="animate-spin" aria-hidden="true" />Waiting for the transfer</span>
                    </div>
                )}

                <p className="ns-t-meta mt-3">USDC on Solana mainnet only. Credits are added the moment the transfer is confirmed, usually within a minute. The request stays good for an hour.</p>
            </div>
        </div>
    );
};
